import { useCallback, useEffect, useRef, useState } from "react";
import type { Map as LeafletMap, CircleMarker } from "leaflet";
import { buscarLugares, descreverPonto, type Coords, type Lugar } from "@/lib/places";
import { IconCheckSmall, IconClose, IconPin, IconTarget } from "./icons";
import { Spinner } from "./primitives";

/* ============================================================
   Onde foi? — tela cheia

   Digitar o nome e marcar o ponto são coisas separadas de propósito. O
   OpenStreetMap tem bem menos restaurante cadastrado que o Google, então
   "não achei na busca" tem que continuar sendo um caminho que funciona:
   escreve o nome, toca no mapa, pronto.

   O Leaflet entra por import dinâmico. São ~150 KB que só interessam a
   quem abre esta tela — no pacote principal atrasariam a abertura do app
   para todo mundo.
   ============================================================ */

export interface LugarEscolhido { name: string; coords: Coords | null }

const SP: [number, number] = [-23.5505, -46.6333];

export function PlacePicker(
  { inicial, onFechar }: { inicial: LugarEscolhido; onFechar: (v: LugarEscolhido | null) => void },
) {
  const caixaMapa = useRef<HTMLDivElement>(null);
  const mapa = useRef<LeafletMap | null>(null);
  const pino = useRef<CircleMarker | null>(null);
  const campo = useRef<HTMLInputElement>(null);

  const [nome, setNome] = useState(inicial.name);
  const [coords, setCoords] = useState<Coords | null>(inicial.coords);
  const [achados, setAchados] = useState<Lugar[] | null>(null);
  const [buscando, setBuscando] = useState(false);
  const [erroBusca, setErroBusca] = useState("");
  const [mapaMorto, setMapaMorto] = useState(false);
  const [gpsOcupado, setGpsOcupado] = useState(false);
  const [descobrindo, setDescobrindo] = useState(false);

  /* ---------- mapa ---------- */

  // Guarda o valor mais recente para o callback de clique do Leaflet, que
  // é registrado uma vez só e enxergaria o estado da primeira renderização.
  const nomeAgora = useRef(nome);
  nomeAgora.current = nome;

  const marcar = useCallback((ponto: Coords, recentrar = true) => {
    setCoords(ponto);
    const L = (window as unknown as { L?: typeof import("leaflet") }).L;
    const m = mapa.current;
    if (!m || !L) return;

    const latlng: [number, number] = [ponto.lat, ponto.lng];
    if (pino.current) pino.current.setLatLng(latlng);
    else {
      pino.current = L.circleMarker(latlng, {
        radius: 10, color: "#fff", weight: 3,
        fillColor: getComputedStyle(document.documentElement)
          .getPropertyValue("--brand").trim() || "#4F7A2E",
        fillOpacity: 1,
      }).addTo(m);
    }
    if (recentrar) m.setView(latlng, Math.max(m.getZoom(), 16));
  }, []);

  /* Marcou um ponto sem ter digitado nome? Descobrimos o endereço — senão
     o lugar não teria como aparecer no guia nem no mapa do grupo. */
  const nomearPeloPonto = useCallback(async (ponto: Coords) => {
    if (nomeAgora.current.trim()) return;
    setDescobrindo(true);
    const { name: achado } = await descreverPonto(ponto);
    setDescobrindo(false);
    if (achado && !nomeAgora.current.trim()) setNome(achado);
  }, []);

  useEffect(() => {
    let vivo = true;

    (async () => {
      try {
        const L = await import("leaflet");
        await import("leaflet/dist/leaflet.css");
        if (!vivo || !caixaMapa.current) return;

        // O Leaflet lê `window.L` em alguns caminhos internos.
        (window as unknown as { L: typeof L }).L = L;

        const m = L.map(caixaMapa.current, { attributionControl: true, zoomControl: true });
        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          maxZoom: 19, attribution: "&copy; OpenStreetMap",
        }).addTo(m);
        m.setView(inicial.coords ? [inicial.coords.lat, inicial.coords.lng] : SP,
          inicial.coords ? 16 : 11);
        m.on("click", (e) => {
          const ponto = {
            lat: Number(e.latlng.lat.toFixed(6)),
            lng: Number(e.latlng.lng.toFixed(6)),
          };
          marcar(ponto);
          void nomearPeloPonto(ponto);
        });

        mapa.current = m;
        if (inicial.coords) marcar(inicial.coords, false);
        setTimeout(() => m.invalidateSize(), 60);

        /* Centraliza o mapa em você — mas só se a permissão de GPS já
           tiver sido dada antes. Abrir a tela pedindo permissão assusta;
           a ideia aqui é só deixar a busca mais relevante para quem já
           liberou. */
        if (!inicial.coords) {
          const p = await navigator.permissions?.query({ name: "geolocation" }).catch(() => null);
          if (p?.state === "granted") {
            navigator.geolocation.getCurrentPosition(
              (pos) => { if (vivo && !pino.current) m.setView([pos.coords.latitude, pos.coords.longitude], 14); },
              () => {},
              { timeout: 6000 },
            );
          }
        }
      } catch {
        if (vivo) setMapaMorto(true);
      }
    })();

    return () => {
      vivo = false;
      mapa.current?.remove();
      mapa.current = null;
      pino.current = null;
    };
    // Roda uma vez: o mapa é montado na abertura da tela.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------- busca ---------- */

  /* Ponto de referência da busca: o ponto já marcado, senão o centro do
     mapa. É o que faz "Bar do Zé" trazer o bar da sua cidade. */
  const referencia = useCallback((): Coords | null => {
    if (coords) return coords;
    const c = mapa.current?.getCenter();
    return c ? { lat: c.lat, lng: c.lng } : null;
  }, [coords]);

  useEffect(() => {
    const termo = nome.trim();
    if (termo.length < 3) { setAchados(null); setErroBusca(""); return; }

    const t = setTimeout(async () => {
      setBuscando(true);
      setErroBusca("");
      try {
        setAchados(await buscarLugares(termo, referencia()));
      } catch {
        setAchados(null);
        setErroBusca("Busca indisponível agora. Digite o nome e marque o ponto no mapa.");
      } finally {
        setBuscando(false);
      }
    }, 700);

    return () => clearTimeout(t);
    // `referencia` muda junto com coords; buscar de novo só por causa
    // disso seria uma chamada de rede a cada toque no mapa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nome]);

  function usarGps() {
    if (!navigator.geolocation) { setErroBusca("Seu navegador não tem GPS."); return; }
    setGpsOcupado(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGpsOcupado(false);
        const ponto = {
          lat: Number(pos.coords.latitude.toFixed(6)),
          lng: Number(pos.coords.longitude.toFixed(6)),
        };
        marcar(ponto);
        void nomearPeloPonto(ponto);
      },
      () => { setGpsOcupado(false); setErroBusca("Não consegui pegar sua localização."); },
      { enableHighAccuracy: true, timeout: 8000 },
    );
  }

  function escolher(l: Lugar) {
    setNome(l.name);
    setAchados(null);
    marcar(l.coords);
  }

  function tirarPonto() {
    setCoords(null);
    pino.current?.remove();
    pino.current = null;
  }

  return (
    <div className="picker">
      <div className="picker-top">
        <button className="topbar-btn" onClick={() => onFechar(null)} aria-label="Fechar">
          <IconClose size={24} />
        </button>
        <div className="topbar-title">Onde foi?</div>
        <button className="topbar-action" onClick={() => onFechar({ name: nome.trim(), coords })}>
          Pronto
        </button>
      </div>

      <div className="picker-search">
        <span className="ico"><IconPin size={20} /></span>
        <input
          ref={campo} value={nome} autoFocus autoComplete="off" spellCheck={false}
          placeholder={descobrindo ? "Descobrindo o endereço…" : "Buscar restaurante ou endereço"}
          onChange={(e) => setNome(e.target.value)}
        />
        {nome && (
          <button className="picker-clear" onClick={() => { setNome(""); campo.current?.focus(); }}
            aria-label="Limpar">
            <IconClose size={18} />
          </button>
        )}
      </div>

      <div className="picker-actions">
        <button className="btn btn-white" onClick={usarGps} disabled={gpsOcupado}>
          <IconTarget size={18} /> {gpsOcupado ? "Procurando…" : "Usar minha localização"}
        </button>
      </div>

      <div className="picker-results">
        {buscando && <div className="picker-empty">Buscando…</div>}
        {!buscando && erroBusca && <div className="picker-empty">{erroBusca}</div>}
        {!buscando && !erroBusca && achados?.length === 0 && (
          <div className="picker-empty">
            Não achei esse lugar no mapa aberto do OpenStreetMap — ele tem bem
            menos restaurante cadastrado que o Google.<br />
            Pode deixar o nome que você digitou e tocar no mapa pra marcar o ponto.
          </div>
        )}
        {!buscando && achados?.map((l, i) => (
          <button key={`${l.name}-${i}`} className="picker-item" onClick={() => escolher(l)}>
            <span className="ico"><IconPin size={18} /></span>
            <span className="txt">
              <span className="n">{l.name}</span>
              {l.detail && <span className="d">{l.detail}</span>}
            </span>
          </button>
        ))}
      </div>

      <div className="picker-map">
        {mapaMorto
          ? <div className="picker-empty">
              Mapa indisponível — sem internet. Você ainda pode digitar o nome do lugar.
            </div>
          : <div ref={caixaMapa} style={{ width: "100%", height: "100%" }} />}
        {!mapaMorto && !mapa.current && <Spinner />}
      </div>

      <div className="picker-foot">
        {coords ? (
          <>
            <span className="ok"><IconCheckSmall size={15} /> Ponto marcado no mapa</span>
            <button className="link-brand" onClick={tirarPonto}>Remover ponto</button>
          </>
        ) : (
          <span>Sem ponto no mapa — o lugar entra só na lista do guia.</span>
        )}
      </div>
    </div>
  );
}
